import { useEffect, useMemo, useState } from 'react';
import { VkLogin } from '../components/VkLogin';

interface Client {
  name: string;
  description?: string;
  logo?: string;
}
interface Me {
  firstName?: string;
  lastName?: string;
  avatar?: string;
}

const SCOPE_TRANSLATIONS: Record<string, { title: string; description: string }> = {
  openid: { title: 'Базовый доступ', description: 'Ваш уникальный идентификатор' },
  profile: { title: 'Профиль', description: 'Имя и аватар вашего профиля' },
  email: { title: 'Email', description: 'Ваш email адрес' },
  offline_access: { title: 'Офлайн доступ', description: 'Доступ, когда вы не в сети' },
};

const Cube = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5Z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);
const Check = () => (
  <svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
  </svg>
);

type Phase = 'check' | 'login' | 'consent' | 'error';

export function AuthorizePage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [phase, setPhase] = useState<Phase>('check');
  const [client, setClient] = useState<Client | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const scopes = (params.get('scope') || 'openid').split(' ').filter(Boolean);

  const loadClient = () => {
    const clientId = params.get('client_id');
    fetch(`/api/oauth/client-info?client_id=${encodeURIComponent(clientId!)}`)
      .then((r) => r.json())
      .then((c) => {
        setClient(c);
        setPhase('consent');
      })
      .catch(() => {
        setError('Не удалось загрузить информацию о приложении');
        setPhase('error');
      });
  };

  const checkAuth = () => {
    fetch('/api/user/me', { credentials: 'include' })
      .then(async (r) => {
        if (r.ok) {
          try {
            setMe(await r.json());
          } catch {}
          loadClient();
        } else {
          setPhase('login');
        }
      })
      .catch(() => setPhase('login'));
  };

  useEffect(() => {
    if (!params.get('client_id') || !params.get('redirect_uri')) {
      setError('Неверные параметры запроса');
      setPhase('error');
      return;
    }
    checkAuth();
  }, []);

  const handleAllow = async () => {
    setSubmitting(true);
    try {
      const res = await fetch('/api/oauth/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: params.get('client_id'),
          redirect_uri: params.get('redirect_uri'),
          scope: params.get('scope') || 'openid',
          state: params.get('state') || undefined,
          code_challenge: params.get('code_challenge') || undefined,
          code_challenge_method: params.get('code_challenge_method') || undefined,
          consent: 'allow',
        }),
      });
      if (res.status === 401) {
        setPhase('login');
        setSubmitting(false);
        return;
      }
      const result = await res.json();
      if (result.redirect_uri) window.location.href = result.redirect_uri;
      else throw new Error();
    } catch {
      setError('Ошибка при подтверждении авторизации');
      setSubmitting(false);
    }
  };

  const handleDeny = () => {
    try {
      const url = new URL(params.get('redirect_uri')!);
      url.searchParams.set('error', 'access_denied');
      url.searchParams.set('error_description', 'Пользователь отклонил запрос');
      const state = params.get('state');
      if (state) url.searchParams.set('state', state);
      window.location.href = url.toString();
    } catch {}
  };

  /* ---- состояния ---- */
  if (phase === 'check') {
    return (
      <div className="auth-bg">
        <div className="auth-spin" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="auth-bg">
        <div className="auth-card">
          <p className="auth-error">{error || 'Произошла ошибка'}</p>
        </div>
      </div>
    );
  }

  if (phase === 'login') {
    return (
      <div className="auth-bg">
        <div className="auth-card">
          <div className="auth-head">
            <div className="auth-logo">
              <Cube />
            </div>
            <h1 className="auth-title">Войдите в TARS</h1>
            <p className="auth-sub">Чтобы продолжить авторизацию — войдите через VK ID</p>
          </div>
          <VkLogin onSuccess={checkAuth} />
        </div>
      </div>
    );
  }

  // consent
  return (
    <div className="auth-bg">
      <div className="auth-card">
        <div className="auth-head">
          <div className="auth-logo">
            {client?.logo ? <img src={client.logo} alt="" style={{ width: '100%', height: '100%', borderRadius: 16, objectFit: 'cover' }} /> : <Cube />}
          </div>
          <h1 className="auth-title">Доступ к аккаунту</h1>
          <p className="auth-sub">
            <b style={{ color: '#e9ecf5' }}>{client?.name || 'Приложение'}</b> запрашивает доступ
          </p>
        </div>

        {me && (
          <div className="auth-user">
            {me.avatar && <img src={me.avatar} alt="" />}
            <span>
              Вы вошли как {me.firstName} {me.lastName}
            </span>
          </div>
        )}

        {client?.description && <div className="auth-client">{client.description}</div>}

        <div className="auth-section-t">Приложение получит доступ к</div>
        <div>
          {scopes.map((s) => {
            const t = SCOPE_TRANSLATIONS[s] || { title: s, description: 'Дополнительные права' };
            return (
              <div className="auth-scope" key={s}>
                <span className="auth-scope-ico">
                  <Check />
                </span>
                <div>
                  <div className="auth-scope-t">{t.title}</div>
                  <div className="auth-scope-d">{t.description}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="auth-actions">
          <button className="auth-btn auth-btn-ghost" onClick={handleDeny} disabled={submitting}>
            Отклонить
          </button>
          <button className="auth-btn auth-btn-primary" onClick={handleAllow} disabled={submitting}>
            {submitting ? 'Подтверждение…' : 'Разрешить'}
          </button>
        </div>

        <p className="auth-foot">Доступ можно отозвать в настройках аккаунта</p>
      </div>
    </div>
  );
}
